import React from 'react';
import { 
  Box, 
  Card,
  CardContent,
  Typography, 
  TextField, 
  Grid 
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { BRAND } from '../../theme/brandTokens';

const getFieldSx = (hasError) => ({
  '& .MuiOutlinedInput-root': {
    height: '48px',
    minHeight: '48px',
    borderRadius: '8px',
    backgroundColor: '#fff',
    '& .MuiOutlinedInput-notchedOutline': {
      borderColor: hasError ? '#f44336' : BRAND.line,
      borderWidth: '2px'
    },
    '&:hover .MuiOutlinedInput-notchedOutline': {
      borderColor: hasError ? '#f44336' : BRAND.accent
    },
    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
      borderColor: hasError ? '#f44336' : BRAND.accent,
      borderWidth: '2px'
    }
  },
  '& .MuiOutlinedInput-input': {
    padding: '12px 16px',
    fontSize: '16px'
  },
  '& .MuiFormHelperText-root': {
    marginLeft: 0,
    marginRight: 0
  }
});

const StoreLocationInfo = ({ formData, updateFormData, errors, setErrors, mode = 'admin' }) => {
  const { t } = useTranslation();
  const isSelfService = mode === 'self-service';
  const hasAddressValue = [formData.address, formData.city, formData.state, formData.zipCode, formData.country, formData.door, formData.additionalInfo]
    .some((value) => value && value.trim());
  const areCoreAddressFieldsRequired = !isSelfService || hasAddressValue;
  const formatFieldLabel = (label, required = false) => (required ? `${label} *` : label);

  const clearFieldError = (fieldName) => {
    if (!setErrors || !errors[fieldName]) {
      return;
    }

    setErrors((prev) => ({ ...prev, [fieldName]: undefined }));
  };

  const handleFieldChange = (fieldName, errorField = fieldName) => (event) => {
    updateFormData({ [fieldName]: event.target.value });
    clearFieldError(errorField);
  };

  const renderTextField = ({ id, label, value, onChange, error, helperText, required = false }) => {
    const displayLabel = formatFieldLabel(label, required);

    return (
      <>
        <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
          {displayLabel}
        </Typography>
        <TextField
          id={id}
          fullWidth
          placeholder={label}
          value={value}
          onChange={onChange}
          error={!!error}
          helperText={helperText}
          sx={getFieldSx(!!error)}
        />
      </>
    );
  };

  return (
    <Box id="location-info-step" sx={{ mt: 2 }}>
      <Typography variant="h5" sx={{ mb: 1, color: BRAND.text, fontWeight: 600 }}>
        {t('storeRegistrationSteps.step3.title')}
      </Typography>
      <Typography variant="body1" sx={{ mb: 4, color: BRAND.textMuted }}>
        {t('storeRegistrationSteps.step3.description')}
      </Typography>

      <Card sx={{ mb: 3 }}>
        <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
          <Grid container spacing={2.5}>
            <Grid size={{ xs: 12, md: 9 }}>
              {renderTextField({
                id: 'store-registration-address-input',
                label: t('storeRegistration.address'),
                value: formData.address,
                onChange: handleFieldChange('address'),
                error: errors.address,
                helperText: errors.address,
                required: areCoreAddressFieldsRequired,
              })}
            </Grid>

            <Grid size={{ xs: 12, md: 3 }}>
              {renderTextField({
                id: 'store-registration-door-input',
                label: t('storeRegistration.door'),
                value: formData.door || '',
                onChange: handleFieldChange('door'),
                error: errors.door,
                helperText: errors.door,
              })}
            </Grid>

            <Grid size={{ xs: 12, md: 6 }}>
              {renderTextField({
                id: 'store-registration-additional-info-input',
                label: t('storeRegistration.additionalInfo'),
                value: formData.additionalInfo || '',
                onChange: handleFieldChange('additionalInfo'),
                error: errors.additionalInfo,
                helperText: errors.additionalInfo,
              })}
            </Grid>

            <Grid size={{ xs: 12, md: 6 }}>
              {renderTextField({
                id: 'store-registration-zip-code-input',
                label: t('storeRegistration.zipCode'),
                value: formData.zipCode,
                onChange: handleFieldChange('zipCode'),
                error: errors.zipCode,
                helperText: errors.zipCode,
                required: areCoreAddressFieldsRequired,
              })}
            </Grid>

            <Grid size={{ xs: 12, md: 4 }}>
              {renderTextField({
                id: 'store-registration-city-input',
                label: t('storeRegistration.city'),
                value: formData.city,
                onChange: handleFieldChange('city'),
                error: errors.city,
                helperText: errors.city,
                required: areCoreAddressFieldsRequired,
              })}
            </Grid>

            <Grid size={{ xs: 12, md: 4 }}>
              {renderTextField({
                id: 'store-registration-state-input',
                label: t('storeRegistration.state'),
                value: formData.state,
                onChange: handleFieldChange('state'),
                error: errors.state,
                helperText: errors.state,
                required: areCoreAddressFieldsRequired,
              })}
            </Grid>

            <Grid size={{ xs: 12, md: 4 }}>
              {renderTextField({
                id: 'store-registration-country-input',
                label: t('storeRegistration.country'),
                value: formData.country,
                onChange: handleFieldChange('country'),
                error: errors.country,
                helperText: errors.country,
                required: areCoreAddressFieldsRequired,
              })}
            </Grid>
          </Grid>
        </CardContent>
      </Card>
    </Box>
  );
};

export default StoreLocationInfo;
